import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { isInteractionRequiredAuthError } from './isInteractionRequiredAuthError';
import { logoutRequest } from './msalConfig';
import { trackException } from '../../app/telemetry/appInsights';

const resolveBrowserAccount = (msalInstance: IPublicClientApplication): AccountInfo => {
    const activeAccount = msalInstance.getActiveAccount();

    if (activeAccount) {
        return activeAccount;
    }

    const cachedAccounts = msalInstance.getAllAccounts();
    const fallbackAccount = cachedAccounts[0];

    if (!fallbackAccount) {
        throw new Error('No active or cached account. The user must be authenticated before acquiring a token.');
    }

    msalInstance.setActiveAccount(fallbackAccount);
    return fallbackAccount;
};


/**
 * Browser token acquisition: silent → acquireTokenRedirect → logoutRedirect on failure.
 *
 * On InteractionRequiredAuthError, acquireTokenRedirect navigates away from the app.
 * On return, MSAL's handleRedirectPromise (called in ensureBrowserAuthenticatedSessionAsync)
 * completes the flow. If the redirect itself fails, logoutRedirect clears stale MSAL state
 * so UnauthenticatedTemplate renders Login.tsx for the user to retry.
 *
 * Ref: https://learn.microsoft.com/entra/identity-platform/scenario-spa-acquire-token
 */
export const acquireBrowserTokenAsync = async (
    msalInstance: IPublicClientApplication,
    scopes: string[],
): Promise<string> => {
    const account = resolveBrowserAccount(msalInstance);

    try {
        const result = await msalInstance.acquireTokenSilent({ scopes, account });
        return result.accessToken;
    } catch (silentError) {
        if (isInteractionRequiredAuthError(silentError)) {
            try {
                // Navigates away to AAD; code after this will not execute on success.
                await msalInstance.acquireTokenRedirect({ scopes, account });
                return ''; // unreachable — redirect navigates away
            } catch {
                // Redirect itself failed (blocked, misconfigured, etc.).
                // Clear stale auth state so Login.tsx is shown on next page load.
                await msalInstance.logoutRedirect(logoutRequest);
                return ''; // unreachable — redirect navigates away
            }
        }
        throw silentError;
    }
};

/**
 * Non-redirect browser token acquisition: silent → acquireTokenPopup → rethrow.
 * Use for secondary scopes (e.g. Nexus) acquired after the user is already logged in.
 * acquireTokenPopup does not navigate away, so it cannot cause a redirect loop.
 */
export const acquireBrowserTokenSilentOrPopupAsync = async (
    msalInstance: IPublicClientApplication,
    scopes: string[],
): Promise<string> => {
    const account = resolveBrowserAccount(msalInstance);

    try {
        const result = await msalInstance.acquireTokenSilent({ scopes, account });
        return result.accessToken;
    } catch (silentError) {
        if (isInteractionRequiredAuthError(silentError)) {
            const result = await msalInstance.acquireTokenPopup({ scopes, account });
            return result.accessToken;
        }
        throw silentError;
    }
};

/**
 * Teams (NAA) token acquisition: silent → acquireTokenPopup (broker-managed) → rethrow.
 * Ref: https://learn.microsoft.com/microsoftteams/platform/concepts/authentication/nested-authentication
 */
export const acquireTeamsTokenAsync = async (
    msalInstance: IPublicClientApplication,
    scopes: string[],
): Promise<string> => {
    try {
        const result = await msalInstance.acquireTokenSilent({ scopes });
        return result.accessToken;
    } catch (silentError) {
        trackException(silentError, {
            source: 'acquireTeamsTokenAsync',
            environment: 'Teams',
        });
        if (isInteractionRequiredAuthError(silentError)) {
            // The Teams broker manages the consent/MFA/sign-in dialog.
            const result = await msalInstance.acquireTokenPopup({ scopes });
            return result.accessToken;
        }
        throw silentError;
    }
};
